import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  User,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  UserCredential,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword as firebaseUpdatePassword,
} from 'firebase/auth';
import { auth } from '@core/config/firebase';
import { graphqlClient } from '@core/api/graphqlClient';
import { gql } from '@apollo/client';
import { SHOPID } from '@core/config/constants';

export interface UserData {
  id: string;
  name: string;
  mobile: string;
  shop_id: string;
  user_type: string;
  created_at: string;
  updated_at: string;
}

interface GetUserByEmailResponse {
  mst_users: UserData[];
}

interface InsertUserResponse {
  insert_mst_users_one: UserData;
}

interface UpdateUserResponse {
  update_mst_users_by_pk: Partial<UserData>;
}

interface AuthContextType {
  user: User | null;
  userData: UserData | null;
  loading: boolean;
  signUp: (email: string, password: string, name: string, mobile: string) => Promise<UserCredential>;
  signIn: (email: string, password: string) => Promise<UserCredential>;
  logout: () => Promise<void>;
  refreshUserData: () => Promise<void>;
  updateUserData: (id: string, name: string, mobile: string) => Promise<boolean>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<{ success: boolean; message: string }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

const GET_USER_BY_EMAIL = gql`
  query GetUserByEmail($email: String!, $shopId: uuid!) {
    mst_users(
      where: {
        email: { _eq: $email }
        shop_id: { _eq: $shopId }
      }
      limit: 1
    ) {
      id
      name
      mobile
      shop_id
      user_type
      created_at
      updated_at
    }
  }
`;

const INSERT_USER = gql`
  mutation InsertUser($user: mst_users_insert_input!) {
    insert_mst_users_one(object: $user) {
      id
      name
      mobile
      shop_id
      user_type
      created_at
      updated_at
    }
  }
`;

const UPDATE_USER = gql`
  mutation UpdateUser($id: uuid!, $name: String!, $mobile: String!) {
    update_mst_users_by_pk(
      pk_columns: { id: $id }
      _set: { name: $name, mobile: $mobile }
    ) {
      id
      name
      mobile
      updated_at
    }
  }
`;

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const fetchUserData = async (email: string): Promise<void> => {
    try {
      const result = await graphqlClient.query({
        query: GET_USER_BY_EMAIL,
        variables: { email, shopId: SHOPID },
        fetchPolicy: 'network-only',
      }) as { data: GetUserByEmailResponse };

      if (result.data?.mst_users && result.data.mst_users.length > 0) {
        setUserData(result.data.mst_users[0] as UserData);
      } else {
        // User not found in DB — do NOT auto-create here.
        // Creation only happens during explicit signUp.
        console.warn('User not found in DB for email:', email);
        setUserData(null);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      setUserData(null);
    }
  };

  const createUserInDatabase = async (
    email: string,
    name: string,
    mobile: string,
    _firebaseUid: string
  ): Promise<UserData | null> => {
    try {
      const userInput: any = {
        name: name || 'User',
        mobile: mobile || '',
        shop_id: SHOPID,
        user_type: 'customer',
      };

      if (email) {
        userInput.email = email;
      }

      const result = await graphqlClient.mutate({
        mutation: INSERT_USER,
        variables: { user: userInput },
      }) as { data: InsertUserResponse };

      return result.data?.insert_mst_users_one as UserData | null;
    } catch (error: any) {
      console.error('Error creating user in database:', error);

      if (error.graphQLErrors?.some((e: any) => e.message?.includes('email'))) {
        try {
          const result = await graphqlClient.mutate({
            mutation: INSERT_USER,
            variables: {
              user: {
                name: name || 'User',
                mobile: mobile || '',
                shop_id: SHOPID,
                user_type: 'customer',
              },
            },
          }) as { data: InsertUserResponse };
          return result.data?.insert_mst_users_one as UserData | null;
        } catch (retryError) {
          console.error('Error creating user without email:', retryError);
          return null;
        }
      }

      return null;
    }
  };

  const signUp = async (
    email: string,
    password: string,
    name: string,
    mobile: string
  ): Promise<UserCredential> => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(userCredential.user, { displayName: name });
    const newUserData = await createUserInDatabase(email, name, mobile, userCredential.user.uid);
    if (newUserData) {
      setUserData(newUserData);
    }
    return userCredential;
  };

  const signIn = async (email: string, password: string): Promise<UserCredential> => {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    // onAuthStateChanged will fire automatically and call fetchUserData.
    // Do NOT call fetchUserData here to avoid duplicate creation.
    return userCredential;
  };

  const logout = async (): Promise<void> => {
    await signOut(auth);
    setUserData(null);
  };

  const refreshUserData = async (): Promise<void> => {
    if (user?.email) {
      await fetchUserData(user.email);
    }
  };

  /**
   * Update name and mobile of an existing user in mst_users
   */
  const updateUserData = async (id: string, name: string, mobile: string): Promise<boolean> => {
    try {
      const result = await graphqlClient.mutate({
        mutation: UPDATE_USER,
        variables: { id, name, mobile },
      }) as { data: UpdateUserResponse };

      if (result.data?.update_mst_users_by_pk) {
        setUserData((prev) =>
          prev ? { ...prev, ...result.data.update_mst_users_by_pk } : prev
        );
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error updating user data:', error);
      return false;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);

      if (firebaseUser?.email) {
        await fetchUserData(firebaseUser.email);
      } else {
        setUserData(null);
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        userData,
        loading,
        signUp,
        signIn,
        logout,
        refreshUserData,
        updateUserData,
        changePassword: async (currentPassword, newPassword) => {
          if (!auth.currentUser) return { success: false, message: 'No authenticated user found.' };
          
          const currentUser = auth.currentUser;
          const email = currentUser.email;

          if (!email) {
            return { 
              success: false, 
              message: 'This account does not have an associated email address. Password updates are only supported for email-based accounts.' 
            };
          }

          try {
            // 🔐 Re-authenticate the user before updating password
            const credential = EmailAuthProvider.credential(email, currentPassword);
            await reauthenticateWithCredential(currentUser, credential);
            
            // 🚀 Update the password in Firebase
            await firebaseUpdatePassword(currentUser, newPassword);
            
            return { success: true, message: 'Password updated successfully!' };
          } catch (error: any) {
            console.error('Password update error:', error);
            
            let message = 'Failed to update password. Please try again.';
            
            if (error.code === 'auth/wrong-password') {
              message = 'The current password you entered is incorrect.';
            } else if (error.code === 'auth/weak-password') {
              message = 'The new password is too weak. Please use at least 6 characters.';
            } else if (error.code === 'auth/requires-recent-login') {
              message = 'For security reasons, please log out and log back in before changing your password.';
            } else if (error.code === 'auth/user-mismatch') {
              message = 'Authentication mismatch. Please ensure you are logged into the correct account.';
            } else if (error.message) {
              message = error.message;
            }
            
            return { success: false, message };
          }
        },
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
